import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useWallet } from '../../contexts/WalletContext';

const WatchlistTrading = () => {
  const { tokens } = useAuth();
  const { wallets, activeWallet } = useWallet();
  const { showSuccess, showError, showTransaction } = useToast();
  const [watchlist, setWatchlist] = useState([]);
  const [selectedToken, setSelectedToken] = useState(null);
  const [tradingPair, setTradingPair] = useState('SOL'); // 'SOL' or 'USDC'
  const [tradeType, setTradeType] = useState('buy');
  const [amount, setAmount] = useState('');
  const [equivalentAmount, setEquivalentAmount] = useState('');
  const [slippage, setSlippage] = useState(0.5);
  const [quote, setQuote] = useState(null);
  const [isGettingQuote, setIsGettingQuote] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isTrading, setIsTrading] = useState(false);
  const [error, setError] = useState(null);
  const [showTradeModal, setShowTradeModal] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';

  // Token mint addresses
  const TOKENS = {
    SOL: 'So11111111111111111111111111111111111111112',
    USDC: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'
  };

  useEffect(() => {
    if (tokens?.access_token) {
      fetchWatchlist();
    }
  }, [tokens]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchWatchlist = async () => {
    try {
      const data = await makeAuthenticatedRequest('/tokens/watchlist');
      setWatchlist(data.data.watchlist);
    } catch (error) {
      setError(error.message);
    }
  };

  const openTradeModal = (token, type = 'buy') => {
    setSelectedToken(token);
    setTradeType(type);
    setShowTradeModal(true);
    setAmount('');
    setEquivalentAmount('');
    setQuote(null);
    setError(null);
  };

  const closeTradeModal = () => {
    setShowTradeModal(false);
    setSelectedToken(null);
    setQuote(null);
    setEquivalentAmount('');
    setError(null);
  };

  const calculateEquivalentAmount = async (inputAmount) => {
    if (!selectedToken || !inputAmount || parseFloat(inputAmount) <= 0) {
      setEquivalentAmount('');
      return;
    }

    try {
      setIsCalculating(true);
      
      const inputMint = tradeType === 'buy' ? TOKENS[tradingPair] : selectedToken.token_address;
      const outputMint = tradeType === 'buy' ? selectedToken.token_address : TOKENS[tradingPair];
      const inputDecimals = tradeType === 'buy' ? (tradingPair === 'SOL' ? 9 : 6) : 6;
      const amountLamports = Math.floor(parseFloat(inputAmount) * Math.pow(10, inputDecimals));

      const data = await makeAuthenticatedRequest('/trading/quote', {
        method: 'POST',
        body: JSON.stringify({
          inputMint,
          outputMint,
          amount: amountLamports,
          slippageBps: Math.floor(slippage * 100)
        })
      });

      const quote = data.data.quote;
      const outputDecimals = tradeType === 'buy' ? 6 : (tradingPair === 'SOL' ? 9 : 6);
      const equivalentAmountFormatted = formatAmount(quote.outputAmount, outputDecimals);
      setEquivalentAmount(equivalentAmountFormatted);
      
      // Also store the quote for later use
      setQuote(quote);
    } catch (error) {
      setEquivalentAmount('Error calculating');
      console.error('Error calculating equivalent amount:', error);
    } finally {
      setIsCalculating(false);
    }
  };

  const handleAmountChange = (value) => {
    setAmount(value);
    
    // Debounce the calculation
    if (value && parseFloat(value) > 0) {
      const timeoutId = setTimeout(() => {
        calculateEquivalentAmount(value);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    } else {
      setEquivalentAmount('');
      setQuote(null);
    }
  };

  const handlePercentageAmount = (percentage) => {
    // For selling, calculate percentage of user's token balance
    // For now, we'll use a mock balance - in production this would come from wallet
    const mockTokenBalance = 10000; // Mock balance for demonstration
    const calculatedAmount = (mockTokenBalance * percentage / 100).toString();
    handleAmountChange(calculatedAmount);
  };

  const getQuote = async () => {
    if (!selectedToken || !amount || !activeWallet) {
      setError('Please enter amount and ensure wallet is connected');
      return;
    }

    // If we already have a quote from the real-time calculation, use it
    if (quote && equivalentAmount) {
      return;
    }

    try {
      setIsGettingQuote(true);
      setError(null);
      
      await calculateEquivalentAmount(amount);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsGettingQuote(false);
    }
  };

  const executeTrade = async () => {
    if (!quote || !activeWallet || !selectedToken) {
      setError('Please get a quote first');
      return;
    }

    try {
      setIsTrading(true);
      setError(null);

      const endpoint = tradeType === 'buy' ? '/trading/buy' : '/trading/sell';
      const payload = tradeType === 'buy' 
        ? {
            tokenMint: selectedToken.token_address,
            solAmount: parseFloat(amount),
            walletId: activeWallet.id,
            slippageBps: Math.floor(slippage * 100)
          }
        : {
            tokenMint: selectedToken.token_address,
            tokenAmount: parseFloat(amount),
            walletId: activeWallet.id,
            slippageBps: Math.floor(slippage * 100)
          };

      const data = await makeAuthenticatedRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      showTransaction(tradeType, selectedToken.token_symbol, amount, data.data.transaction.signature);
      closeTradeModal();
      await fetchWatchlist(); // Refresh watchlist
    } catch (error) {
      setError(error.message);
    } finally {
      setIsTrading(false);
    }
  };

  const formatPrice = (price) => {
    if (price === 0) return '$0.00';
    if (price < 0.01) return `$${price.toFixed(6)}`;
    if (price < 1) return `$${price.toFixed(4)}`;
    return `$${price.toFixed(2)}`;
  };

  const formatNumber = (num) => {
    if (num === 0) return '0';
    if (num >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
    return num.toFixed(2);
  };

  const formatPercentage = (percent) => {
    const formatted = percent.toFixed(2);
    return `${percent >= 0 ? '+' : ''}${formatted}%`;
  };

  const formatAmount = (amount, decimals = 6) => {
    return (amount / Math.pow(10, decimals)).toFixed(6);
  };

  return (
    <div className="watchlist-trading">
      <div className="trading-header">
        <h2>Watchlist Trading</h2>
        <p>Trade your watchlist tokens directly with SOL or USDC</p>
      </div>

      {error && !showTradeModal && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          {error}
          <button className="error-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {!activeWallet && (
        <div className="warning-message">
          <span className="warning-icon">⚠️</span>
          Please select an active platform wallet to start trading
        </div>
      )}

      {watchlist.length === 0 ? (
        <div className="empty-watchlist">
          <div className="empty-icon">📊</div>
          <h3>No Tokens in Watchlist</h3>
          <p>Add tokens to your watchlist first to start trading</p>
        </div>
      ) : (
        <div className="trading-watchlist">
          {watchlist.map((token) => (
            <div key={token.token_address} className="trading-token-card">
              <div className="token-info">
                {token.current_data.image_url && (
                  <img 
                    src={token.current_data.image_url} 
                    alt={token.token_symbol}
                    className="token-image"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
                <div className="token-details">
                  <div className="token-header">
                    <span className="token-symbol">{token.token_symbol}</span>
                    <span className="token-price">{formatPrice(token.current_data.price)}</span>
                  </div>
                  <div className="token-stats">
                    <span className="token-name">{token.token_name}</span>
                    <span className={`price-change ${token.current_data.price_change_24h >= 0 ? 'positive' : 'negative'}`}>
                      {formatPercentage(token.current_data.price_change_24h)}
                    </span>
                  </div>
                  <div className="token-metrics">
                    <span className="metric">
                      <span className="metric-label">Vol:</span>
                      <span className="metric-value">${formatNumber(token.current_data.volume_24h)}</span>
                    </span>
                    <span className="metric">
                      <span className="metric-label">MCap:</span>
                      <span className="metric-value">${formatNumber(token.current_data.market_cap)}</span>
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="trading-actions">
                <button 
                  className="buy-button"
                  onClick={() => openTradeModal(token, 'buy')}
                  disabled={!activeWallet}
                >
                  Buy
                </button>
                <button 
                  className="sell-button"
                  onClick={() => openTradeModal(token, 'sell')}
                  disabled={!activeWallet}
                >
                  Sell
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Trading Modal */}
      {showTradeModal && selectedToken && (
        <div className="modal-overlay">
          <div className="trade-modal">
            <div className="modal-header">
              <h3>
                {tradeType === 'buy' ? 'Buy' : 'Sell'} {selectedToken.token_symbol}
              </h3>
              <button className="close-button" onClick={closeTradeModal}>×</button>
            </div>

            <div className="modal-content">
              {error && (
                <div className="error-message">
                  <span className="error-icon">❌</span>
                  {error}
                </div>
              )}

              <div className="token-display">
                {selectedToken.current_data.image_url && (
                  <img 
                    src={selectedToken.current_data.image_url} 
                    alt={selectedToken.token_symbol}
                    className="modal-token-image"
                  />
                )}
                <div className="modal-token-info">
                  <span className="modal-token-symbol">{selectedToken.token_symbol}</span>
                  <span className="modal-token-price">{formatPrice(selectedToken.current_data.price)}</span>
                </div>
              </div>

              <div className="trade-form">
                <div className="trade-type-selector">
                  <button 
                    className={`trade-type-button ${tradeType === 'buy' ? 'active buy' : ''}`}
                    onClick={() => setTradeType('buy')}
                  >
                    Buy {selectedToken.token_symbol}
                  </button>
                  <button 
                    className={`trade-type-button ${tradeType === 'sell' ? 'active sell' : ''}`}
                    onClick={() => setTradeType('sell')}
                  >
                    Sell {selectedToken.token_symbol}
                  </button>
                </div>

                <div className="trading-pair-selector">
                  <label>Trading Pair</label>
                  <div className="pair-buttons">
                    <button 
                      className={`pair-button ${tradingPair === 'SOL' ? 'active' : ''}`}
                      onClick={() => setTradingPair('SOL')}
                    >
                      {selectedToken.token_symbol}/SOL
                    </button>
                    <button 
                      className={`pair-button ${tradingPair === 'USDC' ? 'active' : ''}`}
                      onClick={() => setTradingPair('USDC')}
                    >
                      {selectedToken.token_symbol}/USDC
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    {tradeType === 'buy' 
                      ? `${tradingPair} Amount` 
                      : `${selectedToken.token_symbol} Amount`
                    }
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={amount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder={tradeType === 'buy' ? '0.1' : '1000'}
                    disabled={isTrading}
                  />
                  
                  {/* Quick Amount Buttons */}
                  <div className="quick-amount-buttons">
                    {tradeType === 'buy' ? (
                      // SOL amount buttons for buying
                      <>
                        <button 
                          className="quick-amount-btn"
                          onClick={() => handleAmountChange('0.1')}
                          disabled={isTrading}
                        >
                          0.1 {tradingPair}
                        </button>
                        <button 
                          className="quick-amount-btn"
                          onClick={() => handleAmountChange('0.5')}
                          disabled={isTrading}
                        >
                          0.5 {tradingPair}
                        </button>
                        <button 
                          className="quick-amount-btn"
                          onClick={() => handleAmountChange('1')}
                          disabled={isTrading}
                        >
                          1 {tradingPair}
                        </button>
                        <button 
                          className="quick-amount-btn"
                          onClick={() => handleAmountChange('2')}
                          disabled={isTrading}
                        >
                          2 {tradingPair}
                        </button>
                        <button 
                          className="quick-amount-btn"
                          onClick={() => handleAmountChange('3')}
                          disabled={isTrading}
                        >
                          3 {tradingPair}
                        </button>
                      </>
                    ) : (
                      // Percentage buttons for selling
                      <>
                        <button 
                          className="quick-amount-btn percentage"
                          onClick={() => handlePercentageAmount(25)}
                          disabled={isTrading}
                        >
                          25%
                        </button>
                        <button 
                          className="quick-amount-btn percentage"
                          onClick={() => handlePercentageAmount(50)}
                          disabled={isTrading}
                        >
                          50%
                        </button>
                        <button 
                          className="quick-amount-btn percentage"
                          onClick={() => handlePercentageAmount(75)}
                          disabled={isTrading}
                        >
                          75%
                        </button>
                        <button 
                          className="quick-amount-btn percentage"
                          onClick={() => handlePercentageAmount(100)}
                          disabled={isTrading}
                        >
                          100%
                        </button>
                      </>
                    )}
                  </div>
                  
                  {equivalentAmount && (
                    <div className="equivalent-amount">
                      <span className="equivalent-label">
                        ≈ {equivalentAmount} {tradeType === 'buy' ? selectedToken.token_symbol : tradingPair}
                      </span>
                      {isCalculating && <span className="calculating">Calculating...</span>}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>Slippage Tolerance (%)</label>
                  <select
                    value={slippage}
                    onChange={(e) => setSlippage(parseFloat(e.target.value))}
                    disabled={isTrading}
                  >
                    <option value={0.1}>0.1%</option>
                    <option value={0.5}>0.5%</option>
                    <option value={1.0}>1.0%</option>
                    <option value={2.0}>2.0%</option>
                    <option value={5.0}>5.0%</option>
                  </select>
                </div>

                <button 
                  className="quote-button"
                  onClick={getQuote}
                  disabled={!amount || isGettingQuote}
                >
                  {isGettingQuote ? 'Getting Quote...' : 'Get Quote'}
                </button>

                {quote && (
                  <div className="quote-display">
                    <div className="quote-item">
                      <span>You {tradeType}:</span>
                      <span>
                        {tradeType === 'buy' 
                          ? `${formatAmount(quote.inputAmount, tradingPair === 'SOL' ? 9 : 6)} ${tradingPair}`
                          : `${formatAmount(quote.inputAmount, 6)} ${selectedToken.token_symbol}`
                        }
                      </span>
                    </div>
                    <div className="quote-item">
                      <span>You receive:</span>
                      <span>
                        {tradeType === 'buy' 
                          ? `${formatAmount(quote.outputAmount, 6)} ${selectedToken.token_symbol}`
                          : `${formatAmount(quote.outputAmount, tradingPair === 'SOL' ? 9 : 6)} ${tradingPair}`
                        }
                      </span>
                    </div>
                    <div className="quote-item">
                      <span>Price Impact:</span>
                      <span className={quote.priceImpact > 5 ? 'high-impact' : ''}>
                        {formatPercentage(quote.priceImpact)}
                      </span>
                    </div>

                    <button 
                      className={`execute-button ${tradeType}`}
                      onClick={executeTrade}
                      disabled={isTrading}
                    >
                      {isTrading ? 'Executing...' : `Execute ${tradeType.toUpperCase()}`}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WatchlistTrading;